export const UNIVERSE_IDS = ['rise', 'r-node', 'sx-business', 'elina'] as const;
export type UniverseId = (typeof UNIVERSE_IDS)[number];
export type Capability = { id: string; title: string; detail: string; group: string };
export const ELINA_MISSIONS = [
  'Vente',
  'Technique',
  'Maintenance',
  'Formation',
  'Copilote',
] as const;
export const ELINA_COPILOT: Capability = {
  id: 'elina-copilot',
  title: 'M’appuyer sur Elina Copilote',
  detail:
    'Commerciaux, intervenants et direction : préparer les rendez-vous et les interventions, retrouver les informations utiles et éclairer les décisions à partir de vos données autorisées.',
  group: 'Mes missions',
};
export type Universe = {
  id: UniverseId;
  name: string;
  number: string;
  verb: string;
  descriptor: string;
  summary: string;
  headline: string;
  highlight: string;
  description: string;
  clarity: string;
  outcomes: { title: string; detail: string }[];
  capabilities: Capability[];
};
export const UNIVERSES: Universe[] = [
  {
    id: 'rise',
    name: 'RISE',
    number: '01',
    verb: 'Développer mes relations et mes ventes',
    descriptor: 'L’interface de R-Node avec vos prospects et partenaires',
    summary:
      'Portail public, site interactif ou showroom : des parcours personnalisés reliés à votre noyau métier, avec ou sans vente en ligne.',
    headline: 'Vos prospects et partenaires,',
    highlight: 'reliés à votre activité.',
    description:
      'RISE assure l’interface de R-Node avec vos prospects, vos clients et vos partenaires. Il permet de présenter votre offre, de guider chaque interlocuteur et de donner une suite aux demandes, aux échanges et aux commandes. Les parcours s’adaptent à vos publics, avec ou sans vente en ligne.',
    clarity: 'Du premier intérêt à une relation durable.',
    outcomes: [
      {
        title: 'Un accueil qui oriente',
        detail: 'Votre offre, vos services ou votre mission se comprennent selon les besoins de chaque public.',
      },
      {
        title: 'Un parcours qui avance',
        detail: 'Demandes, rendez-vous, inscriptions ou commandes : chaque interaction trouve sa suite.',
      },
      {
        title: 'Une relation qui continue',
        detail: 'Des espaces dédiés, des échanges suivis et des communications adaptées entretiennent le lien.',
      },
    ],
    capabilities: [
      {
        id: 'rise-site',
        title: 'Présenter mes services et capter les demandes',
        detail: 'Site sur mesure, pages d’offres ou de mission et formulaires utiles.',
        group: 'Mon interface',
      },
      {
        id: 'rise-shop',
        title: 'Vendre mes produits en ligne',
        detail: 'Catalogue, panier et parcours de commande.',
        group: 'Mon interface',
      },
      {
        id: 'rise-b2b',
        title: 'Ouvrir un espace dédié à mes publics',
        detail: 'Accès réservé, contenus et services selon les profils à préciser.',
        group: 'Mon interface',
      },
      {
        id: 'rise-booking',
        title: 'Prendre des rendez-vous',
        detail: 'Un parcours de réservation adapté à votre activité.',
        group: 'Mes relations',
      },
      {
        id: 'rise-crm',
        title: 'Suivre mes contacts et les prochaines actions',
        detail: 'Demandes qualifiées et continuité avec votre CRM.',
        group: 'Mes relations',
      },
      {
        id: 'rise-retention',
        title: 'Entretenir la relation avec mes publics',
        detail: 'Parcours de suivi et communications adaptés à votre communauté.',
        group: 'Mes relations',
      },
    ],
  },
  {
    id: 'r-node',
    name: 'R-Node',
    number: '02',
    verb: 'Relier et piloter mon activité',
    descriptor: 'Le noyau digital métier de votre entreprise',
    summary: 'Votre savoir-faire, vos règles et vos opérations prennent forme dans un noyau métier conçu grâce à la méthode SX.',
    headline: 'Votre expertise prend forme',
    highlight: 'dans le cœur digital de votre activité.',
    description:
      'R-Node est le noyau digital métier de votre entreprise. Conçu grâce à la méthode SX, il traduit finement votre savoir-faire, vos règles et vos critères de décision dans les parcours de travail de vos équipes. Ventes, projets, production ou interventions : les opérations partagent les informations utiles et les responsabilités sont explicites.',
    clarity: 'Toute votre activité. Une même continuité.',
    outcomes: [
      {
        title: 'Partager les repères du métier',
        detail:
          'Les personnes concernées retrouvent les informations, les règles et l’historique nécessaires pour poursuivre un dossier.',
      },
      {
        title: 'Préserver la continuité du travail',
        detail:
          'Les étapes, les validations et les passages de relais deviennent visibles entre les équipes, les projets et les sites.',
      },
      {
        title: 'Éclairer vos décisions',
        detail:
          'La direction relie les indicateurs aux opérations. Élina aide les commerciaux, les intervenants et la direction à préparer les analyses et les actions utiles.',
      },
    ],
    capabilities: [
      {
        id: 'rnode-dashboard',
        title: 'Piloter depuis un cockpit unifié',
        detail: 'Indicateurs, vues par métier et rapports d’activité.',
        group: 'Piloter',
      },
      {
        id: 'rnode-crm',
        title: 'Centraliser la relation client',
        detail: 'Contacts, opportunités et historique commercial.',
        group: 'Piloter',
      },
      {
        id: 'rnode-sales',
        title: 'Relier ventes, commandes et livraisons',
        detail: 'Du document commercial à la préparation et au suivi.',
        group: 'Opérer',
      },
      {
        id: 'rnode-stock',
        title: 'Gérer les stocks et les inventaires',
        detail: 'Mouvements, lots et comptages adaptés à vos processus.',
        group: 'Opérer',
      },
      {
        id: 'rnode-purchase',
        title: 'Organiser les achats et réceptions',
        detail: 'Fournisseurs, approvisionnements et disponibilité.',
        group: 'Opérer',
      },
      {
        id: 'rnode-production',
        title: 'Suivre la production et la traçabilité',
        detail: 'Matières, fabrication, lots et contrôles.',
        group: 'Opérer',
      },
      {
        id: 'rnode-teams',
        title: 'Coordonner les équipes et les sites',
        detail: 'Planning, responsabilités et droits par utilisateur.',
        group: 'Connecter',
      },
      {
        id: 'rnode-connect',
        title: 'Connecter mes outils existants',
        detail: 'Échanges avec vos logiciels et reprise des données à qualifier.',
        group: 'Connecter',
      },
    ],
  },
  {
    id: 'sx-business',
    name: 'SX Business',
    number: '03',
    verb: 'Construire ma croissance',
    descriptor: 'Le conseil pour votre offre et votre développement',
    summary: 'Clarifier votre cap, faire évoluer votre offre et organiser votre développement.',
    headline: 'Votre ambition mérite',
    highlight: 'une trajectoire claire.',
    description:
      'SX Business relie votre positionnement, votre offre et votre modèle économique à un plan de croissance concret. Nous partons de votre entreprise, de ses clients et de ses priorités.',
    clarity: 'Le bon cap. Les bonnes décisions. Un plan concret.',
    outcomes: [
      {
        title: 'Comprendre votre situation',
        detail: 'Une lecture partagée de vos clients, de vos forces et de vos freins.',
      },
      {
        title: 'Construire votre différence',
        detail: 'Une offre et un positionnement ancrés dans les besoins réels du marché.',
      },
      {
        title: 'Organiser votre croissance',
        detail: 'Des priorités, une feuille de route et des indicateurs pour suivre les avancées.',
      },
    ],
    capabilities: [
      {
        id: 'sx-diagnostic',
        title: 'Faire le point sur mon activité',
        detail: 'Diagnostic des enjeux, des freins et des opportunités.',
        group: 'Mon cap',
      },
      {
        id: 'sx-positioning',
        title: 'Clarifier mon positionnement',
        detail: 'Clients prioritaires, besoins et différenciation.',
        group: 'Mon cap',
      },
      {
        id: 'sx-offer',
        title: 'Concevoir ou faire évoluer mon offre',
        detail: 'Proposition de valeur et expérience de service.',
        group: 'Mon offre',
      },
      {
        id: 'sx-model',
        title: 'Structurer mon modèle économique',
        detail: 'Tarification, revenus et leviers de rentabilité.',
        group: 'Mon offre',
      },
      {
        id: 'sx-growth',
        title: 'Construire mon plan de croissance',
        detail: 'Acquisition, développement commercial et priorités.',
        group: 'Mon développement',
      },
      {
        id: 'sx-launch',
        title: 'Préparer le lancement d’une activité',
        detail: 'Hypothèses, expérimentation et feuille de route.',
        group: 'Mon développement',
      },
    ],
  },
  {
    id: 'elina',
    name: 'Élina',
    number: '04',
    verb: 'Faire équipe avec l’IA',
    descriptor: 'L’IA métier intégrée à R-Node',
    summary:
      'Une assistance pour vos commerciaux, vos intervenants et votre direction, à partir des connaissances et des accès autorisés.',
    headline: 'Votre expertise accompagne',
    highlight: 'vos équipes et votre direction.',
    description:
      'Élina est l’IA métier intégrée à R-Node. Elle aide vos commerciaux à préparer leurs rendez-vous et leurs réponses, vos intervenants à retrouver le contexte utile et votre direction à examiner les situations et préparer ses décisions.',
    clarity: 'La bonne assistance, au bon endroit.',
    outcomes: [
      {
        title: 'Préparer les actions commerciales',
        detail: 'Retrouver le contexte d’un client, préparer un rendez-vous et construire une réponse à partir des informations autorisées.',
      },
      {
        title: 'Assister les intervenants',
        detail:
          'Retrouver les informations techniques, les procédures et l’historique utiles à une intervention, selon les droits de chaque personne.',
      },
      {
        title: 'Éclairer les décisions de la direction',
        detail:
          'Réunir les éléments pertinents, comprendre une situation et préparer les prochaines actions. Les personnes responsables gardent la validation.',
      },
    ],
    capabilities: [
      {
        id: 'elina-sales',
        title: 'Accompagner mes prospects avec Elina Vente',
        detail: 'Présentation de l’offre, orientation et préparation des demandes.',
        group: 'Mes missions',
      },
      {
        id: 'elina-technical',
        title: 'Assister mes équipes techniques',
        detail: 'Recherche guidée dans la documentation et les procédures.',
        group: 'Mes missions',
      },
      {
        id: 'elina-maintenance',
        title: 'Accompagner la maintenance',
        detail: 'Suivi des équipements et procédures d’intervention.',
        group: 'Mes missions',
      },
      {
        id: 'elina-training',
        title: 'Accompagner la formation',
        detail: 'Explications, exercices et parcours d’apprentissage.',
        group: 'Mes missions',
      },
      ELINA_COPILOT,
      {
        id: 'elina-knowledge',
        title: 'Relier mes documents et connaissances',
        detail: 'Sources choisies et accessibles selon les droits.',
        group: 'Mon environnement',
      },
      {
        id: 'elina-integration',
        title: 'Intégrer Elina à mes interfaces',
        detail: 'Site, plateforme ou espace métier.',
        group: 'Mon environnement',
      },
    ],
  },
];
export function getUniverse(id: string) {
  return UNIVERSES.find((u) => u.id === id);
}
export const CAPABILITIES = UNIVERSES.flatMap((u) =>
  u.capabilities.map((c) => ({ ...c, universe: u.id }))
);
export function selectedCapabilities(ids: string[]) {
  return CAPABILITIES.filter((c) => ids.includes(c.id));
}


